import { Link } from "react-router-dom";
import { ArrowRight, MousePointerClick, Eye, MoveRight, Layers, Layout, Smartphone, Check } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ChatbotWidget from "../components/ChatbotWidget";

const principles = [
  { n: "01", icon: Eye,              title: "One screen. One job.",       text: "Every page has a single primary action. The eye should never have to hunt." },
  { n: "02", icon: MoveRight,        title: "Three taps to action.",      text: "Visitors should reach a phone call, form, or booking in three taps or less — from any page." },
  { n: "03", icon: Layout,           title: "Predictable layout.",        text: "Headers stay put. Buttons stay where buttons go. We don't make people think." },
  { n: "04", icon: MousePointerClick,title: "Bigger tap targets.",        text: "Buttons, links, and form fields sized for thumbs — not desktop mice with surgical aim." },
  { n: "05", icon: Layers,           title: "Cut, then cut again.",       text: "Half the words. Half the sections. Half the choices. What's left is the website that converts." },
  { n: "06", icon: Smartphone,       title: "Mobile decides everything.", text: "70% of your visitors are on phones. We design mobile first, desktop second." },
];

export default function SimplicitySells() {
  return (
    <div className="bg-[#FDFBF7] min-h-screen">
      <Navbar />

      <section className="relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full -z-10 opacity-50"
             style={{ background: "radial-gradient(closest-side, rgba(197,160,89,0.32), transparent 70%)" }}/>
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-12">
          <p className="label-eyebrow">The Framework</p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight mt-4 font-medium leading-[1.05] max-w-4xl">
            Simplicity <em className="text-[#C5A059] not-italic">sells</em>.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-[#5C5C5C] max-w-3xl leading-relaxed">
            The clearer your website, the more it converts. Our framework is built on a simple idea: if a customer has to think about what to do next, you've already lost them. We design websites that guide — never confuse — the people you want to win.
          </p>
        </div>
      </section>

      {/* 6 principles */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {principles.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.n} className="card-flat p-8 rounded-2xl">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#FDFBF7] border border-[#EBE8DF] text-[#C5A059]">
                      <Icon size={18} strokeWidth={1.8}/>
                    </span>
                    <span className="font-mono text-[13px] text-[#C5A059]">{p.n}</span>
                  </div>
                  <h3 className="font-display text-2xl mt-6 leading-tight">{p.title}</h3>
                  <p className="mt-3 text-[15px] text-[#5C5C5C] leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Before / After visual story */}
      <section className="py-20 md:py-24 bg-[#F5F3ED]/60 border-y border-[#EBE8DF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="label-eyebrow">Before · After</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-4 font-medium leading-tight max-w-3xl">
            The same business, redesigned for simplicity.
          </h2>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="p-8 rounded-2xl border border-[#EBE8DF] bg-white">
              <p className="label-eyebrow">Before</p>
              <h3 className="font-display text-2xl mt-2">14 menu items. 6 CTAs. 0 conversions.</h3>
              <ul className="mt-5 space-y-2.5 text-[15px] text-[#5C5C5C]">
                {["Cluttered header with every page linked","Hero is a slideshow nobody waits for","Buttons fight for attention","Phone number buried in the footer","Mobile menu hides everything","Visitor bounces in 8 seconds"].map((x) => (
                  <li key={x} className="flex items-start gap-2"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#D1CDC0] shrink-0"/><span>{x}</span></li>
                ))}
              </ul>
            </div>
            <div className="p-8 rounded-2xl border-2 border-[#C5A059] bg-[#FDFBF7]">
              <p className="label-eyebrow" style={{color:"#C5A059"}}>After — Simplicity Sells</p>
              <h3 className="font-display text-2xl mt-2">4 menu items. 1 hero CTA. Calls every day.</h3>
              <ul className="mt-5 space-y-2.5 text-[15px]">
                {["Clean header — only what matters","Hero with one promise and one action","Buttons follow a clear visual hierarchy","Phone number visible on every screen","Mobile menu has only what people need","Visitors stay, scroll, and convert"].map((x) => (
                  <li key={x} className="flex items-start gap-3"><Check size={16} className="text-[#C5A059] mt-1 shrink-0"/><span>{x}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="label-eyebrow">Ready for a clearer site?</p>
          <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight font-medium">
            Let's design something visitors actually <em className="text-[#C5A059] not-italic">use</em>.
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/pricing" className="btn-primary">Request a custom quote <ArrowRight size={16}/></Link>
            <Link to="/#concepts" className="btn-secondary">See example sites</Link>
          </div>
        </div>
      </section>

      <Footer />
      <ChatbotWidget />
    </div>
  );
}
